/**
 * Shared ontology loader for validator stages.
 *
 * Loads ontology JSON files from the `ontology/` directory shipped alongside
 * the package. Data is provided via static ESM JSON imports — no runtime file
 * IO — so this module is browser-safe (Phase 003, 2026-05-13) and can sit on
 * the import chain reachable from `@cogo/validator/browser`.
 *
 * Usage:
 *   import { loadOntology } from "./ontology-loader"
 *   const eventTypes = loadOntology("event-types.json")
 *
 * Pre-Phase-003 (deprecated): this module used `readFileSync` from `node:fs`,
 * which Vite externalises in browser bundles and throws on access. The
 * transitive chain `browser.ts → jsonb-api → stage-actionflow → stage-actionflow-deep
 * → ontology-loader` then crashed `apps/cogo-studio` at boot in dev mode.
 * See `apps/cogo-studio/.planning/phases/003-validator-browser-purification/SPEC.md`.
 */

import actionTypes from "../../../ontology/action-types.json" with { type: "json" }
import domainFlowPatterns from "../../../ontology/domain-flow-patterns.json" with { type: "json" }
import elementTypes from "../../../ontology/element-types.json" with { type: "json" }
import eventTypes from "../../../ontology/event-types.json" with { type: "json" }
import flowStepTypes from "../../../ontology/flow-step-types.json" with { type: "json" }
import relationshipRules from "../../../ontology/relationship-rules.json" with { type: "json" }
import statePatterns from "../../../ontology/state-patterns.json" with { type: "json" }
import { KNOWN_EXPRESSION_ROOTS } from "../../vocab/expression-namespaces"

// Filename → parsed ontology object. Bundler-cached; one source of truth.
const ONTOLOGY_MAP: Record<string, Record<string, unknown>> = {
  "action-types.json": actionTypes as Record<string, unknown>,
  "domain-flow-patterns.json": domainFlowPatterns as Record<string, unknown>,
  "element-types.json": elementTypes as Record<string, unknown>,
  "event-types.json": eventTypes as Record<string, unknown>,
  "flow-step-types.json": flowStepTypes as Record<string, unknown>,
  "relationship-rules.json": relationshipRules as Record<string, unknown>,
  "state-patterns.json": statePatterns as Record<string, unknown>,
}

/**
 * Load an ontology JSON file by name (e.g., "event-types.json").
 * Returns the parsed JSON object, or null if the filename is not one of the
 * 7 bundled ontology files.
 */
export function loadOntology(filename: string): Record<string, unknown> | null {
  return ONTOLOGY_MAP[filename] ?? null
}

/**
 * How many element types the ontology declares — DERIVED, never pinned.
 *
 * The count was hand-written into at least eight assertion sites across four
 * packages, which is why two of them still said 193 after the 2026-08-08
 * ontology fix took the ontology to 195. Every site that merely needs "the
 * number of element types" imports this; the one place that states the absolute
 * number is `__tests__/element-bind-ontology-contract.test.ts`, which pins it
 * alongside a sha256 of the whole canonical file and is the vacuity floor for
 * everything derived from here.
 */
export const ELEMENT_TYPE_COUNT: number = Object.keys(
  (elementTypes as { types: Record<string, unknown> }).types,
).length

/**
 * Build a Set from ontology object keys.
 * Example: loadOntologyKeys("event-types.json", "events") → Set(["tap", "change", ...])
 */
export function loadOntologyKeys(filename: string, field: string): Set<string> {
  const data = loadOntology(filename)
  if (!data) return new Set()
  const obj = data[field]
  if (!obj || typeof obj !== "object") return new Set()
  return new Set(Object.keys(obj as Record<string, unknown>))
}

/**
 * @deprecated Phase 003 (2026-05-13) — ontology data is now loaded via
 * static ESM JSON imports, so there is no cache to reset. This function is
 * preserved as a no-op for backward compatibility with tests that call it
 * in `beforeEach` (see `__tests__/ontology-loader-slots.test.ts`).
 */
export function _resetOntologyCache(): void {
  // Intentional no-op: static JSON imports are module-cached by ESM
  // semantics; there is no runtime cache state to clear.
}

// ─── Fallback constants (defense-in-depth when JSON fields are missing) ───

/** Roots that are legal at the head of an expression and need no state
 * declaration. Copy #4 of 5 (pre-fold) was a hand-typed list here that omitted
 * `local`, `props` AND `computed`; it is now the one composed vocabulary
 * (src/vocab/expression-namespaces.ts).
 *
 * Note for whoever reads `loadRuntimeNamespaces` below: the ontology override it
 * looks for (`state-patterns.json` → `runtimeNamespaces`) does not exist in that
 * file, so this list — not the ontology — is what every caller actually gets. */
const RUNTIME_NS_FALLBACK = [...KNOWN_EXPRESSION_ROOTS]

/** Auto-triggered flow triggers (lifecycle hooks, not user events) */
const AUTO_TRIGGER_FALLBACK = ["init", "lifecycle", "mount", "unmount", "onLoad", "onMount", "onBlockLoad"]

/** Interactive element types requiring explicit event bindings */
const INTERACTIVE_TYPES_FALLBACK = ["button", "select", "checkbox", "radio", "switch"]

/** Valid setState operations.
 *
 * Includes "replace" as an explicit full-array-overwrite marker. The runtime's
 * default branch (`action-flow-engine.ts` switch default) treats both omitted
 * and unrecognised operations as a full `state.setState(target, value)` —
 * behaviourally identical to "replace". Accepting "replace" lets W5-regenerate
 * flows declare full-replace load semantics explicitly without tripping R5a. */
const SET_STATE_OPS_FALLBACK = ["set", "append", "replace", "upsert", "removeWhere", "updateWhere"]

/* The two `*_SLOT_FALLBACK` arrays that used to live here are gone. Their own
 * doc comments said they "mirror ctype:card / ctype:sidebar cogo:slot in
 * semantic-types-v1.ttl" — i.e. they were declared copies, and the TTL copy
 * they mirrored has itself been deleted (no query read it). `slots` in
 * element-types.json is now the single home of the shadcn preset-zone
 * vocabulary. A fallback that restates the data it is a fallback FOR cannot
 * be defence in depth: if the static JSON import ever failed, the fallback
 * would silently supply a second, unversioned answer instead of failing. */

/**
 * Load runtime namespaces from ontology with fallback.
 */
export function loadRuntimeNamespaces(): Set<string> {
  const ontology = loadOntology("state-patterns.json")
  const runtimeNs = (ontology?.runtimeNamespaces ?? null) as string[] | null
  if (runtimeNs && Array.isArray(runtimeNs)) return new Set(runtimeNs)
  return new Set(RUNTIME_NS_FALLBACK)
}

/**
 * Load auto-trigger types from ontology with fallback.
 */
export function loadAutoTriggers(): Set<string> {
  const ontology = loadOntology("flow-step-types.json")
  const triggerTypes = (ontology?.triggerTypes ?? null) as string[] | null
  if (triggerTypes && Array.isArray(triggerTypes)) return new Set(triggerTypes)
  return new Set(AUTO_TRIGGER_FALLBACK)
}

/**
 * Load interactive element types requiring an explicit EVENT binding
 * (`events.json`, checked by `contract-unbound-interactive`) from ontology
 * with fallback.
 *
 * Reads the explicit `interactive: true` fact on each element-types.json
 * entry. Does NOT infer interactivity from `category`: a prior heuristic
 * (`category === "form" && !requiredProps.includes("bind")`) misclassified
 * non-interactive form wrapper/label types as interactive, producing
 * systematic false positives on `contract-unbound-interactive` (M6-L1
 * attribution, 2026-08-10: `docs/superpowers/reports/2026-08-10-m7-
 * attribution-loops/M6-L1-attribution.md`).
 *
 * **Exclusion criterion (M6-R1 fix-round-1, adversarial review, 2026-08-11
 * — the first pass over-narrowed this to 6 types and silenced 7 real
 * controls; see `.tmp-m6r1-adversarial-review.md` I1)**: a type carries NO
 * `interactive` fact ONLY when EITHER
 *   (a) it is a wrapper/label/content-region type that renders no control of
 *       its own — field, field-label, field-content, field-group, label
 *       (all `category:"form"`, no `requiredProps`; the leaf control they
 *       contain carries the fact and is still flagged when unbound), OR
 *   (b) an unconditional runtime auto-sync is proven by RENDERER SOURCE, not
 *       assumed from the ontology shape — `input`/`textarea` are the only
 *       two: `cogo-react-renderer`'s renderers call
 *       `useBoundValue(element.bind, ...)` unconditionally on every native
 *       change (`bind` is REQUIRED for both, so the sync always runs); an
 *       `events.json` handler is an ADDITIONAL flow on top of that sync, not
 *       a precondition for it. Flagging a bind-only input/textarea as
 *       "unbound" would be a false positive with no runtime consequence
 *       (confirmed against `__tests__/fixtures/semantic-valid`'s bound
 *       `search-input` with no events.json entry).
 * Every OTHER control type gets the fact, INCLUDING types whose `bind` is
 * merely optional (`requiredProps: []`) — optional `bind` does not prove
 * auto-sync the way REQUIRED `bind` does, so the (b) exemption does not
 * extend to them. Concretely: `toggle`, `star-rating`, `chip-input`,
 * `color-picker`, `month-picker`, `phone-input`, `input-otp` are leaf
 * controls with optional `bind` — they carry the fact.
 *
 * Set membership is pinned directly (not just the ontology file's sha256) by
 * `__tests__/element-bind-ontology-contract.test.ts`'s
 * `INTERACTIVE_EVENT_BINDING_TYPES` assertion, so a future over- or
 * under-narrowing of this set fails a test that names the set, not only a
 * byte-hash that any edit — wanted or not — moves.
 */
export function loadInteractiveTypes(): Set<string> {
  const ontology = loadOntology("element-types.json")
  if (!ontology) return new Set(INTERACTIVE_TYPES_FALLBACK)
  const types = ontology.types as Record<string, Record<string, unknown>> | undefined
  if (!types) return new Set(INTERACTIVE_TYPES_FALLBACK)
  const interactive = new Set<string>()
  for (const [typeName, spec] of Object.entries(types)) {
    if (spec.interactive === true) interactive.add(typeName)
  }
  for (const t of INTERACTIVE_TYPES_FALLBACK) interactive.add(t)
  return interactive
}

/**
 * Load valid setState operations from ontology with fallback.
 */
export function loadValidOperations(): Set<string> {
  const ontology = loadOntology("action-types.json")
  if (ontology) {
    const actions = ontology.actions as Record<string, Record<string, unknown>> | undefined
    const setState = actions?.setState as Record<string, unknown> | undefined
    const ops = setState?.validOperations as string[] | undefined
    if (ops && Array.isArray(ops)) return new Set(ops)
  }
  return new Set(SET_STATE_OPS_FALLBACK)
}

/**
 * Load slot element-types for a parent component (e.g. "card" → Set{"card-header", "card-content", "card-footer"}).
 *
 * TTL/JSON stores bare slot names ("header" / "content" / "footer"); this function composes the parent-prefixed
 * element.type form that the validator consumes via seenTypes.has() checks.
 *
 * Returns an empty Set for unknown parentTypes — callers must handle that case explicitly rather than relying
 * on silent fallback to unrelated data. There is deliberately no per-parent fallback list any more: the two
 * that were here restated the very rows they claimed to back up.
 */
export function loadSlotTypes(parentType: string): Set<string> {
  const ontology = loadOntology("element-types.json")
  const types = ontology?.types as Record<string, Record<string, unknown>> | undefined
  const slots = types?.[parentType]?.slots as string[] | undefined
  if (slots && Array.isArray(slots)) {
    return new Set(slots.map((slot) => `${parentType}-${slot}`))
  }
  // Unknown parent — empty Set (no silent pollution of validator state).
  return new Set()
}
